/**
 * HyperMem admin TanStack Query hooks (v2 port, Jun 6, 2026).
 *
 * One hook per BE endpoint behind the HyperMem admin surface. Tenant-scoped
 * reads take ``tenantId: string | null`` (null = platform-wide aggregate where
 * the endpoint supports it). Lazy via ``enabled`` so each tab only fetches when
 * mounted. Mutations invalidate the relevant query keys.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  approveWorkflowSuggestion,
  getAdminNudgeStats,
  getHypermemAudit,
  getHypermemExperiments,
  getHypermemFacts,
  getHypermemJobs,
  getHypermemState,
  getHypermemStateByTenant,
  getReasoningTraceSummary,
  getWorkflowSuggestionDetail,
  listAdminNudges,
  listReasoningTraces,
  listWorkflowSuggestions,
  rejectWorkflowSuggestion,
  type AdminNudgeStatus,
  type ApproveSuggestionRequest,
  type HypermemTenantSortColumn,
  type NudgeType,
  type ReasoningOutcome,
  type WorkflowSuggestionStatus,
} from "@ando/core";

export const adminHypermemKeys = {
  root: ["admin", "hypermem"] as const,
  state: (t: string | null) => ["admin", "hypermem", "state", t] as const,
  byTenant: (k: string) => ["admin", "hypermem", "by-tenant", k] as const,
  facts: (pillar: string, t: string, k: string) =>
    ["admin", "hypermem", "facts", pillar, t, k] as const,
  jobs: (t: string | null) => ["admin", "hypermem", "jobs", t] as const,
  experiments: (t: string) => ["admin", "hypermem", "experiments", t] as const,
  audit: (t: string, k: string) => ["admin", "hypermem", "audit", t, k] as const,
  suggestions: (t: string, status: string) =>
    ["admin", "hypermem", "suggestions", t, status] as const,
  suggestionDetail: (id: string) =>
    ["admin", "hypermem", "suggestion", id] as const,
  nudges: (t: string, k: string) => ["admin", "hypermem", "nudges", t, k] as const,
  nudgeStats: (t: string, days: number) =>
    ["admin", "hypermem", "nudge-stats", t, days] as const,
  reasoningTraces: (t: string, k: string) =>
    ["admin", "hypermem", "reasoning-traces", t, k] as const,
  reasoningTraceSummary: (turnId: string) =>
    ["admin", "hypermem", "reasoning-trace-summary", turnId] as const,
};

export function useHypermemState(tenantId: string | null, enabled = true) {
  return useQuery({
    queryKey: adminHypermemKeys.state(tenantId),
    queryFn: () => getHypermemState(tenantId),
    enabled,
    staleTime: 60_000,
  });
}

export function useHypermemTenants(
  opts: {
    limit?: number;
    offset?: number;
    sortBy?: HypermemTenantSortColumn;
    sortDir?: "asc" | "desc";
  },
  enabled = true,
) {
  return useQuery({
    queryKey: adminHypermemKeys.byTenant(JSON.stringify(opts)),
    queryFn: () => getHypermemStateByTenant(opts),
    enabled,
    staleTime: 60_000,
  });
}

export function useHypermemFacts(
  pillar: "episodic" | "semantic",
  tenantId: string,
  opts: { user_id?: string; limit?: number; offset?: number },
  enabled = true,
) {
  return useQuery({
    queryKey: adminHypermemKeys.facts(pillar, tenantId, JSON.stringify(opts)),
    queryFn: () => getHypermemFacts(pillar, tenantId, opts),
    enabled,
    staleTime: 30_000,
  });
}

export function useHypermemJobs(
  tenantId: string | null,
  limit = 20,
  enabled = true,
) {
  return useQuery({
    queryKey: adminHypermemKeys.jobs(tenantId),
    queryFn: () =>
      getHypermemJobs({ tenant_id: tenantId ?? undefined, limit }),
    enabled,
    staleTime: 30_000,
  });
}

export function useHypermemExperiments(tenantId: string, enabled = true) {
  return useQuery({
    queryKey: adminHypermemKeys.experiments(tenantId),
    queryFn: () => getHypermemExperiments(tenantId),
    enabled,
    staleTime: 60_000,
  });
}

export function useHypermemAudit(
  tenantId: string,
  opts: { event_type?: string; user_id?: string; limit?: number },
  enabled = true,
) {
  return useQuery({
    queryKey: adminHypermemKeys.audit(tenantId, JSON.stringify(opts)),
    queryFn: () => getHypermemAudit(tenantId, opts),
    enabled,
    staleTime: 30_000,
  });
}

export function useWorkflowSuggestions(
  tenantId: string,
  status: WorkflowSuggestionStatus | string = "pending",
  enabled = true,
) {
  return useQuery({
    queryKey: adminHypermemKeys.suggestions(tenantId, status),
    queryFn: () => listWorkflowSuggestions(tenantId, status),
    enabled,
    staleTime: 30_000,
  });
}

export function useWorkflowSuggestionDetail(
  suggestionId: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: adminHypermemKeys.suggestionDetail(suggestionId ?? ""),
    queryFn: () => getWorkflowSuggestionDetail(suggestionId as string),
    enabled: enabled && !!suggestionId,
    staleTime: 30_000,
  });
}

export function useApproveWorkflowSuggestion(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body?: ApproveSuggestionRequest;
    }) => approveWorkflowSuggestion(id, body),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["admin", "hypermem", "suggestions", tenantId],
      });
    },
  });
}

export function useRejectWorkflowSuggestion(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      rejectWorkflowSuggestion(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["admin", "hypermem", "suggestions", tenantId],
      });
    },
  });
}

export function useAdminNudges(
  tenantId: string,
  opts: {
    status?: AdminNudgeStatus;
    nudgeType?: NudgeType;
    limit?: number;
    offset?: number;
  },
  enabled = true,
) {
  return useQuery({
    queryKey: adminHypermemKeys.nudges(tenantId, JSON.stringify(opts)),
    queryFn: () => listAdminNudges(tenantId, opts),
    enabled,
    staleTime: 30_000,
  });
}

export function useAdminNudgeStats(
  tenantId: string,
  days = 7,
  enabled = true,
) {
  return useQuery({
    queryKey: adminHypermemKeys.nudgeStats(tenantId, days),
    queryFn: () => getAdminNudgeStats(tenantId, days),
    enabled,
    staleTime: 60_000,
  });
}

// ── Reasoning traces (HyperMem Layer 3 — agent_reasoning_traces) ────────

export function useReasoningTraces(
  tenantId: string,
  opts: {
    user_id?: string;
    outcome?: ReasoningOutcome;
    intent?: string;
    min_success_confidence?: number;
    max_success_confidence?: number;
    limit?: number;
    offset?: number;
  },
  enabled = true,
) {
  return useQuery({
    queryKey: adminHypermemKeys.reasoningTraces(tenantId, JSON.stringify(opts)),
    queryFn: () => listReasoningTraces(tenantId, opts),
    enabled,
    staleTime: 30_000,
  });
}

export function useReasoningTraceSummary(
  turnId: string | null,
  userId: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: adminHypermemKeys.reasoningTraceSummary(turnId ?? ""),
    queryFn: () => getReasoningTraceSummary(turnId as string, userId as string),
    enabled: enabled && !!turnId && !!userId,
    staleTime: 30_000,
  });
}
